/**
 * Camera with gesture-aware orbit controls.
 *
 * Why custom vs OrbitControls?
 *  - We need clean gesture conflict resolution (drag horizontal=rotate, vertical=scroll page)
 *  - We want momentum and damping consistent with our motion language
 *  - OrbitControls is ~10KB and we override a third of its behavior anyway
 */

import * as THREE from "three";
import { Signal } from "@/state/Store";

export interface CameraState {
  yaw: number; // radians
  pitch: number; // radians
  zoom: number; // 1.0 = default
}

/** Per-product camera setup (from ProductConfig.viewer). Standard defaults
 *  when fields are omitted: fov 25, target (0,0.4,0), zoom 0.6-1.6, pitch 5-75. */
export interface CameraOptions {
  fov?: number;
  minZoom?: number;
  maxZoom?: number;
  minPitchDeg?: number;
  maxPitchDeg?: number;
  target?: { x: number; y: number; z: number };
  /** Bounding-sphere fit multiplier in frameBox(). model-viewer fits the sphere
   *  EXACTLY to the framed FOV (no slack) → 1.0 is the faithful match. Larger =
   *  zoomed out (the old hardcoded 1.3 was the wrong-zoom regression). */
  frameMargin?: number;
  /** If set (e.g. 0.9), frameBox() places the camera so the model's WIDTH fills this
   *  fraction of the horizontal viewport — then backs off only if the bounding sphere
   *  would clip, so the model always fits. Absent → pure sphere fit (frameMargin). */
  fitWidthFraction?: number;
}

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const MIN_PITCH = 5 * DEG_TO_RAD;
const MAX_PITCH = 75 * DEG_TO_RAD;
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 1.6;

export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  readonly state: Signal<CameraState>;
  private target = new THREE.Vector3(0, 0.4, 0);
  private targetOverride: THREE.Vector3 | null = null;
  private minZoom = MIN_ZOOM;
  private maxZoom = MAX_ZOOM;
  // Configured zoom-in cap (floor); frameBox() raises maxZoom per-model so the camera can
  // always dolly to a fixed close distance from the SURFACE regardless of model size — a big
  // U-shape frames further out, so a static cap left it un-zoomable. See frameBox().
  private baseMaxZoom = MAX_ZOOM;
  private minPitch = MIN_PITCH;
  private maxPitch = MAX_PITCH;
  private frameMargin = 1.0;
  private fitWidthFraction = 0;
  private baseDistance = 3.2;
  // Default framing captured at the last frameBox() — restored by resetView() when
  // leaving a part focus. focusRAF drives the focus/reset glide (separate from the
  // inertia rAF); cancelled on pointerdown so grabbing the model takes over.
  private homeTarget = new THREE.Vector3(0, 0.4, 0);
  private homeDistance = 3.2;
  private focusRAF = 0;
  // True while a focal view (centerOn) is active — a tap on the viewer then returns
  // to the normal framing. Cleared by any reframe (reset/home/zoom-to-fit/frameBox).
  private focused = false;
  // Set when a gesture ever became a 2-finger pinch, so the final finger-lift after a
  // pinch is NOT mistaken for a tap (which would reset the view).
  private hadPinch = false;
  private element: HTMLElement;
  private isDragging = false;
  private dragStart = { x: 0, y: 0, yaw: 0, pitch: 0, intent: "none" as "none" | "rotate" | "scroll" };
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchStartDist = 0;
  private pinchStartZoom = 1;
  private listeners: Array<() => void> = [];
  // Flick inertia: track the last move sample + angular velocity (rad/ms) so a
  // quick release keeps the sofa spinning and settles (model-viewer feel). The
  // rAF runs ONLY while spinning — see startInertia — so idle battery is untouched.
  private lastMove = { x: 0, y: 0, t: 0 };
  private velYaw = 0;
  private velPitch = 0;
  private inertiaRAF = 0;

  constructor(
    initialYawDeg: number,
    initialPitchDeg: number,
    initialZoom: number,
    element: HTMLElement,
    opts: CameraOptions = {},
  ) {
    const aspect = element.clientWidth / Math.max(1, element.clientHeight);
    // FOV is the "zoom"/lens — Neovaa standard is 25deg (vs three.js default 45).
    this.camera = new THREE.PerspectiveCamera(opts.fov ?? 25, aspect, 0.05, 100);
    this.element = element;
    if (opts.minZoom !== undefined) this.minZoom = opts.minZoom;
    if (opts.maxZoom !== undefined) this.maxZoom = opts.maxZoom;
    this.baseMaxZoom = this.maxZoom;
    if (opts.minPitchDeg !== undefined) this.minPitch = opts.minPitchDeg * DEG_TO_RAD;
    if (opts.maxPitchDeg !== undefined) this.maxPitch = opts.maxPitchDeg * DEG_TO_RAD;
    if (opts.frameMargin !== undefined) this.frameMargin = opts.frameMargin;
    if (opts.fitWidthFraction !== undefined) this.fitWidthFraction = opts.fitWidthFraction;
    if (opts.target) {
      this.target.set(opts.target.x, opts.target.y, opts.target.z);
      this.targetOverride = this.target.clone(); // focal point sticks across reframes
    }
    this.state = new Signal<CameraState>({
      yaw: initialYawDeg * DEG_TO_RAD,
      pitch: clamp(initialPitchDeg * DEG_TO_RAD, this.minPitch, this.maxPitch),
      zoom: clamp(initialZoom, this.minZoom, this.maxZoom),
    });
    this.applyToCamera();
    this.bindGestures();
  }

  /** Sync the camera position from the given rig state (defaults to the current). */
  private applyToCamera(s: CameraState = this.state.get()): void {
    const { yaw, pitch, zoom } = s;
    const distance = this.baseDistance / zoom;
    const x = Math.sin(yaw) * Math.cos(pitch) * distance;
    const z = Math.cos(yaw) * Math.cos(pitch) * distance;
    const y = Math.sin(pitch) * distance + this.target.y;
    this.camera.position.set(x, y, z);
    this.camera.lookAt(this.target);
    // Refresh matrixWorld / matrixWorldInverse NOW so projectToScreen (used by the
    // dimension labels + hotspot dots on the SAME camera-change tick, before the next
    // render) projects against the up-to-date camera, not last frame's.
    this.camera.updateMatrixWorld();
  }

  setState(next: Partial<CameraState>): void {
    const prev = this.state.get();
    const merged: CameraState = {
      yaw: next.yaw ?? prev.yaw,
      pitch: clamp(next.pitch ?? prev.pitch, this.minPitch, this.maxPitch),
      zoom: clamp(next.zoom ?? prev.zoom, this.minZoom, this.maxZoom),
    };
    // Move the camera BEFORE publishing the new state, so subscribers that project on
    // camera-change (Dimensions labels/arrows, hotspot dots) read the CURRENT camera.
    // Publishing first projected them one frame stale → they blanked/jumped on each
    // rotate-start, then snapped back the next frame.
    this.applyToCamera(merged);
    this.state.set(() => merged);
  }

  getYawDeg(): number {
    return this.state.get().yaw * RAD_TO_DEG;
  }
  getPitchDeg(): number {
    return this.state.get().pitch * RAD_TO_DEG;
  }
  getZoom(): number {
    return this.state.get().zoom;
  }

  /** Farthest the camera can dolly from the target (full zoom-out). The studio
   *  room sizes its walls beyond this so the camera never reaches them. */
  getMaxDollyDistance(): number {
    return this.homeDistance / this.minZoom;
  }

  /**
   * Reframe the camera so a bounding box fills the view nicely, regardless of
   * the current orbit angle. Uses the bounding sphere so the fit holds for any
   * yaw/pitch — essential when layouts change size (a U-shape is far bigger
   * than a 1-seater).
   */
  frameBox(box: THREE.Box3): void {
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    // model-viewer targets the bounding-BOX centre (not the sphere centre) by
    // default; a product can still pin a focal point via targetOverride.
    const boxCenter = box.getCenter(new THREE.Vector3());
    this.target.copy(this.targetOverride ?? boxCenter);
    // Keep the projection matrix in sync with the live element size so the fit
    // below measures against the SAME aspect the renderer paints with.
    if (this.element && this.element.clientWidth && this.element.clientHeight) {
      this.camera.aspect = this.element.clientWidth / this.element.clientHeight;
      this.camera.updateProjectionMatrix();
    }
    const vFov = this.camera.fov * DEG_TO_RAD;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    const limitingFov = Math.min(vFov, hFov);
    // model-viewer's idealCameraDistance fits the bounding SPHERE exactly to the
    // framed FOV (radius / sin(fov/2)). frameMargin (~0.95) matches its zoom; the
    // old hardcoded 1.3 pushed the camera ~30% too far (the wrong-zoom regression).
    let dist = (sphere.radius / Math.sin(limitingFov / 2)) * this.frameMargin;
    if (this.fitWidthFraction > 0) {
      // BULLETPROOF FIT — project the 8 box corners through the REAL camera and
      // dolly until the on-screen extent fits: width to fitWidthFraction, height
      // capped so a deep/tall shape never overflows. NDC [-1,1] always maps to the
      // full canvas, so this fills exactly fitWidthFraction of the viewer for ANY
      // size/aspect, and it accounts for perspective + pitch + yaw exactly (the old
      // world-space width/height approximation under-counted the depth of forward
      // chaises and let the U-shape spill off wide-short viewports).
      const c = [
        new THREE.Vector3(box.min.x, box.min.y, box.min.z),
        new THREE.Vector3(box.min.x, box.min.y, box.max.z),
        new THREE.Vector3(box.min.x, box.max.y, box.min.z),
        new THREE.Vector3(box.min.x, box.max.y, box.max.z),
        new THREE.Vector3(box.max.x, box.min.y, box.min.z),
        new THREE.Vector3(box.max.x, box.min.y, box.max.z),
        new THREE.Vector3(box.max.x, box.max.y, box.min.z),
        new THREE.Vector3(box.max.x, box.max.y, box.max.z),
      ];
      const heightFraction = 0.97; // leave a hair of vertical margin
      const tmp = new THREE.Vector3();
      dist = sphere.radius / Math.sin(limitingFov / 2); // safe start: model fully in frustum
      for (let i = 0; i < 5; i++) {
        this.baseDistance = dist;
        this.applyToCamera(); // positions camera + refreshes matrices
        let maxX = 1e-4, maxY = 1e-4;
        for (const corner of c) {
          tmp.copy(corner).project(this.camera);
          maxX = Math.max(maxX, Math.abs(tmp.x));
          maxY = Math.max(maxY, Math.abs(tmp.y));
        }
        const over = Math.max(maxX / this.fitWidthFraction, maxY / heightFraction);
        if (Math.abs(over - 1) < 0.004) break;
        dist *= over; // NDC scales ~1/distance → dolly by the overflow factor
      }
    }
    this.baseDistance = dist;
    this.homeTarget.copy(this.target);
    this.homeDistance = this.baseDistance;
    this.focused = false; // a fresh reframe (shape/size change) exits any focal view
    // Per-model zoom-in cap: let the camera dolly until it sits ~CLOSE_MARGIN from the model's
    // surface (bounding sphere), so EVERY forme — 1-seater to U-shape — reaches the same tight
    // close-up. distance = baseDistance / zoom, so the surface-close distance (radius + margin)
    // maps to zoom = baseDistance / (radius + margin). Never below the configured floor.
    const CLOSE_MARGIN = 0.28; // metres from the surface at full zoom-in
    const surfaceClose = Math.max(sphere.radius + CLOSE_MARGIN, 0.4);
    this.maxZoom = Math.max(this.baseMaxZoom, this.baseDistance / surfaceClose);
    this.applyToCamera();
    // Dev-only fit telemetry: the actual on-screen NDC half-extent of the model
    // (1.0 == canvas edge). Lets a headless check prove the fill % for any size.
    if (typeof window !== "undefined") {
      const dbg = new THREE.Vector3();
      let mx = 0, my = 0;
      for (const corner of [
        new THREE.Vector3(box.min.x, box.min.y, box.min.z),
        new THREE.Vector3(box.min.x, box.min.y, box.max.z),
        new THREE.Vector3(box.min.x, box.max.y, box.min.z),
        new THREE.Vector3(box.min.x, box.max.y, box.max.z),
        new THREE.Vector3(box.max.x, box.min.y, box.min.z),
        new THREE.Vector3(box.max.x, box.min.y, box.max.z),
        new THREE.Vector3(box.max.x, box.max.y, box.min.z),
        new THREE.Vector3(box.max.x, box.max.y, box.max.z),
      ]) {
        dbg.copy(corner).project(this.camera);
        mx = Math.max(mx, Math.abs(dbg.x));
        my = Math.max(my, Math.abs(dbg.y));
      }
      (window as unknown as { __NEOVAA_FIT__?: unknown }).__NEOVAA_FIT__ = {
        widthPct: Math.round(mx * 100),
        heightPct: Math.round(my * 100),
        aspect: Math.round(this.camera.aspect * 100) / 100,
      };
    }
  }

  /** Animate the camera to a pose — optional look-at target (world), dolly
   *  distance, yaw/pitch (radians), zoom. Ease-out; cancels inertia + any prior
   *  programmatic glide. Each step flags needsRender via setState so the on-demand
   *  loop paints it. A pointerdown cancels it (grabbing the model takes over). */
  animateTo(
    to: { target?: THREE.Vector3; distance?: number; yaw?: number; pitch?: number; zoom?: number },
    durationMs = 650,
  ): void {
    // Any programmatic camera glide ends a focal view — except centerOn, which
    // re-sets focused=true AFTER calling this. Centralising the clear here means no
    // future animateTo caller can ever leave the tap-to-reset flag stale.
    this.focused = false;
    if (this.inertiaRAF) { cancelAnimationFrame(this.inertiaRAF); this.inertiaRAF = 0; }
    if (this.focusRAF) { cancelAnimationFrame(this.focusRAF); this.focusRAF = 0; }
    this.velYaw = 0;
    this.velPitch = 0;
    const s = this.state.get();
    const fromTarget = this.target.clone();
    const toTarget = to.target ? to.target.clone() : this.target.clone();
    const fromDist = this.baseDistance;
    const toDist = to.distance ?? this.baseDistance;
    const fromYaw = s.yaw;
    const fromPitch = s.pitch;
    const fromZoom = s.zoom;
    const toPitch = clamp(to.pitch ?? s.pitch, this.minPitch, this.maxPitch);
    const toZoom = clamp(to.zoom ?? s.zoom, this.minZoom, this.maxZoom);
    // Shortest-path yaw so a focus never spins the long way round.
    let dYaw = (to.yaw ?? s.yaw) - fromYaw;
    while (dYaw > Math.PI) dYaw -= 2 * Math.PI;
    while (dYaw < -Math.PI) dYaw += 2 * Math.PI;
    const t0 = nowMs();
    const step = () => {
      const t = Math.min(1, (nowMs() - t0) / durationMs);
      const e = 1 - Math.pow(1 - t, 3); // easeOutCubic
      this.target.lerpVectors(fromTarget, toTarget, e);
      this.baseDistance = fromDist + (toDist - fromDist) * e;
      this.setState({
        yaw: fromYaw + dYaw * e,
        pitch: fromPitch + (toPitch - fromPitch) * e,
        zoom: fromZoom + (toZoom - fromZoom) * e,
      });
      if (t < 1) this.focusRAF = requestAnimationFrame(step);
      else this.focusRAF = 0;
    };
    this.focusRAF = requestAnimationFrame(step);
  }

  /** Dolly + orbit to frame a small region (a product "part") around a world
   *  point. radius = world radius to fit; yaw/pitch in degrees (optional). */
  focusRegion(target: THREE.Vector3, radius: number, yawDeg?: number, pitchDeg?: number, durationMs = 650): void {
    const vFov = this.camera.fov * DEG_TO_RAD;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * this.camera.aspect);
    const limitingFov = Math.min(vFov, hFov);
    const distance = (Math.max(radius, 0.05) / Math.sin(limitingFov / 2)) * this.frameMargin;
    this.animateTo(
      {
        target,
        distance,
        yaw: yawDeg !== undefined ? yawDeg * DEG_TO_RAD : undefined,
        pitch: pitchDeg !== undefined ? pitchDeg * DEG_TO_RAD : undefined,
        zoom: 1,
      },
      durationMs,
    );
  }

  /** Re-center on a world point keeping the CURRENT orbit angle, with a light
   *  dolly-in (distanceFactor × homeDistance). The named part simply becomes the
   *  centre of the frame — no special angle/zoom. Marks the rig "focused" so the
   *  next tap on the viewer snaps back to the normal framing. */
  centerOn(target: THREE.Vector3, distanceFactor = 0.72, durationMs = 600): void {
    this.animateTo(
      { target: target.clone(), distance: this.homeDistance * distanceFactor, zoom: 1 },
      durationMs,
    );
    this.focused = true; // AFTER animateTo (which clears it) so the focal flag sticks
  }

  /** Animate back to the default full-product framing captured at the last frameBox(). */
  resetView(yawDeg: number, pitchDeg: number, durationMs = 600): void {
    this.animateTo(
      { target: this.homeTarget.clone(), distance: this.homeDistance, yaw: yawDeg * DEG_TO_RAD, pitch: pitchDeg * DEG_TO_RAD, zoom: 1 },
      durationMs,
    );
  }

  /** Dolly OUT to `homeDistance * factor` (whole item + slack), keeping the current
   *  yaw/pitch (omitted → animateTo holds them). homeDistance already bakes in the
   *  product's frameMargin, so this stays correct per product and per shape/size. */
  zoomToFit(factor: number, durationMs = 600): void {
    this.animateTo({ target: this.homeTarget.clone(), distance: this.homeDistance * factor, zoom: 1 }, durationMs);
  }

  /** Dolly back to the captured home distance, keeping the current yaw/pitch. */
  returnHome(durationMs = 600): void {
    this.animateTo({ target: this.homeTarget.clone(), distance: this.homeDistance, zoom: 1 }, durationMs);
  }

  bindGestures(): void {
    const el = this.element;
    const onPointerDown = (e: PointerEvent) => {
      // Touching the model STOPS any inertia spin (like grabbing a real spinning
      // object) and resets velocity tracking from this contact point.
      if (this.inertiaRAF) { cancelAnimationFrame(this.inertiaRAF); this.inertiaRAF = 0; }
      if (this.focusRAF) { cancelAnimationFrame(this.focusRAF); this.focusRAF = 0; }
      this.velYaw = 0;
      this.velPitch = 0;
      this.lastMove = { x: e.clientX, y: e.clientY, t: nowMs() };
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (this.pointers.size === 1) {
        this.isDragging = true;
        this.hadPinch = false; // fresh press → candidate tap until it drags or pinches
        const s = this.state.get();
        this.dragStart = { x: e.clientX, y: e.clientY, yaw: s.yaw, pitch: s.pitch, intent: "none" };
        try { el.setPointerCapture(e.pointerId); } catch { /* iOS may refuse */ }
      } else if (this.pointers.size === 2) {
        this.hadPinch = true;
        const [p1, p2] = Array.from(this.pointers.values());
        this.pinchStartDist = dist(p1, p2);
        this.pinchStartZoom = this.state.get().zoom;
        this.isDragging = false; // a 2nd finger means pinch, not orbit
      }
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2) {
        // Pinch zoom
        const [p1, p2] = Array.from(this.pointers.values());
        const d = dist(p1, p2);
        if (this.pinchStartDist > 0) {
          const ratio = d / this.pinchStartDist;
          this.setState({ zoom: this.pinchStartZoom * ratio });
        }
        return;
      }

      if (!this.isDragging) return;

      const dx = e.clientX - this.dragStart.x;
      const dy = e.clientY - this.dragStart.y;

      // Intent detection for gesture-aware behavior on touch.
      if (this.dragStart.intent === "none" && e.pointerType === "touch") {
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        // Clear horizontal intent → rotate IMMEDIATELY, from pixel 1. No dead start
        // (the old 8px wait is what made it feel sticky/laggy to grab).
        if (absX >= absY && absX > 0) {
          this.dragStart.intent = "rotate";
        } else if (absX > 4 || absY > 4) {
          // Vertical-ish drag also ROTATES (pitch). Touch-on-the-model always orbits
          // both axes — the canvas has touch-action:none (Renderer.ts) so the page
          // never fights it; scroll the page by dragging OUTSIDE the viewer. This is
          // what unlocks starting a top/bottom rotation directly (model-viewer parity).
          this.dragStart.intent = "rotate";
        } else {
          return; // sub-4px and not yet horizontally clear: wait (filters taps/jitter)
        }
      } else if (this.dragStart.intent === "none") {
        this.dragStart.intent = "rotate"; // mouse always rotates
      }

      if (this.dragStart.intent === "rotate") {
        // Viewport-normalized rotation gain: a full-WIDTH swipe = K_YAW*π of yaw,
        // a full-HEIGHT swipe = K_PITCH*π of pitch — consistent on any screen/DPR
        // and ~2× snappier than the old fixed dampX=0.005 / dampY=0.003.
        const K_YAW = 1.7;
        const K_PITCH = 0.9;
        const w = this.element.clientWidth || window.innerWidth;
        const h = this.element.clientHeight || window.innerHeight;
        const dampX = (K_YAW * Math.PI) / w;
        const dampY = (K_PITCH * Math.PI) / h;
        // dx>0 (drag right) must rotate the model so it "follows the finger":
        // the camera orbits the opposite way, hence -dx.
        this.setState({
          yaw: this.dragStart.yaw - dx * dampX,
          pitch: this.dragStart.pitch + dy * dampY,
        });
        // Track angular velocity (rad/ms) from the last sample for flick inertia.
        const now = nowMs();
        const dtMove = now - (this.lastMove.t || now);
        if (dtMove > 0 && this.lastMove.t) {
          const mdx = e.clientX - this.lastMove.x;
          const mdy = e.clientY - this.lastMove.y;
          this.velYaw = (-mdx * dampX) / dtMove;
          this.velPitch = (mdy * dampY) / dtMove;
        }
        this.lastMove = { x: e.clientX, y: e.clientY, t: now };
      }
    };
    const endPointer = (e: PointerEvent) => {
      // Defensive: ignore pointers we never tracked. Since up/cancel are bound on
      // window now, they also fire for touches that started outside the canvas.
      if (!this.pointers.delete(e.pointerId)) return;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (this.pointers.size < 2) this.pinchStartDist = 0;
      // 2→1 transition: re-anchor orbit on the SURVIVING finger from its current
      // position + the current camera state, so lifting one finger after a pinch
      // doesn't snap the model (stale dragStart) and rotation resumes immediately.
      if (this.pointers.size === 1) {
        const [id] = this.pointers.keys();
        const p = this.pointers.get(id)!;
        const s = this.state.get();
        this.dragStart = { x: p.x, y: p.y, yaw: s.yaw, pitch: s.pitch, intent: "rotate" };
        this.isDragging = true;
        try { el.setPointerCapture(id); } catch { /* ignore */ }
      }
      if (this.pointers.size === 0) {
        this.isDragging = false;
        // A tap (negligible drag, no pinch) while a focal view is active returns to
        // the normal centred framing — the "click the viewer to go back" gesture.
        // A real drag / flick (≥6px) or a post-pinch lift falls through to inertia.
        const tapDist = Math.hypot(e.clientX - this.dragStart.x, e.clientY - this.dragStart.y);
        const wasTap = !this.hadPinch && this.dragStart.intent !== "rotate" && tapDist < 6;
        this.hadPinch = false;
        if (this.focused && wasTap) {
          this.focused = false;
          this.returnHome(500);
          return;
        }
        // Last finger lifted → if it was a flick, keep spinning and settle.
        this.startInertia();
      }
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const direction = e.deltaY > 0 ? -1 : 1;
      const delta = direction * 0.08;
      this.setState({ zoom: this.state.get().zoom + delta });
    };
    const onKey = (e: KeyboardEvent) => {
      const STEP = 5 * DEG_TO_RAD;
      const s = this.state.get();
      if (e.key === "ArrowLeft") this.setState({ yaw: s.yaw + STEP });
      else if (e.key === "ArrowRight") this.setState({ yaw: s.yaw - STEP });
      else if (e.key === "ArrowUp") this.setState({ pitch: s.pitch + STEP });
      else if (e.key === "ArrowDown") this.setState({ pitch: s.pitch - STEP });
      else if (e.key === "+" || e.key === "=") this.setState({ zoom: s.zoom + 0.1 });
      else if (e.key === "-" || e.key === "_") this.setState({ zoom: s.zoom - 0.1 });
    };

    // pointerdown stays on the canvas (only a touch that STARTS on the model orbits);
    // move/up/cancel live on WINDOW so a finger lifting or moving OUTSIDE the canvas
    // is still seen — on iOS the up for a non-primary touch often never reaches the
    // element, which left a phantom finger in the map (orbit dead, single finger
    // zooming). lostpointercapture covers iOS dropping capture mid-gesture.
    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endPointer);
    window.addEventListener("pointercancel", endPointer);
    el.addEventListener("lostpointercapture", endPointer);
    el.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("keydown", onKey);

    this.listeners.push(() => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", endPointer);
      window.removeEventListener("pointercancel", endPointer);
      el.removeEventListener("lostpointercapture", endPointer);
      el.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKey);
    });
  }

  /** After a flick (fast release), keep rotating with friction decay and settle.
   *  rAF runs ONLY while spinning: it starts on a fast release, stops at EPS, and
   *  is cancelled by the next pointerdown — so the engine's on-demand render loop
   *  stays idle (battery-safe) when nothing is moving. Each step setState() flags
   *  needsRender so the engine actually paints the spin. */
  private startInertia(): void {
    const SPEED_MIN = 0.0006; // rad/ms — below this, a careful drag doesn't spin
    const FRICTION = 0.90;    // per-16ms decay — settles in ~0.45s (snappy, not endless)
    const EPS = 0.0004;       // rad/ms — stop threshold (cut off while imperceptible)
    const MAX_V = 0.009;      // rad/ms — cap coast to ~a quarter-turn so a hard flick
                              //          can't fling half the model away or nauseate
    if (Math.hypot(this.velYaw, this.velPitch) < SPEED_MIN) return;
    this.velYaw = clamp(this.velYaw, -MAX_V, MAX_V);
    this.velPitch = clamp(this.velPitch, -MAX_V, MAX_V);
    let prev = nowMs();
    const stepInertia = () => {
      const now = nowMs();
      const dt = now - prev;
      prev = now;
      const s = this.state.get();
      this.setState({ yaw: s.yaw + this.velYaw * dt, pitch: s.pitch + this.velPitch * dt });
      const decay = Math.pow(FRICTION, dt / 16);
      this.velYaw *= decay;
      this.velPitch *= decay;
      if (Math.hypot(this.velYaw, this.velPitch) > EPS) {
        this.inertiaRAF = requestAnimationFrame(stepInertia);
      } else {
        this.inertiaRAF = 0;
      }
    };
    this.inertiaRAF = requestAnimationFrame(stepInertia);
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(1, height);
    this.camera.updateProjectionMatrix();
  }

  destroy(): void {
    // Cancel any in-flight inertia spin so the rAF can't survive teardown and
    // repaint a disposed scene (SPA unmount / iframe re-init).
    if (this.inertiaRAF) {
      cancelAnimationFrame(this.inertiaRAF);
      this.inertiaRAF = 0;
    }
    if (this.focusRAF) { cancelAnimationFrame(this.focusRAF); this.focusRAF = 0; }
    for (const off of this.listeners) off();
    this.listeners.length = 0;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), hi);
}

function dist(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** High-res timestamp (ms) with a Date fallback — used for velocity + inertia dt. */
function nowMs(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
